import ActionTextIcon from "@/components/icons/ActionTextIcon";
import PopconfirmDeleteIcon from "@/components/icons/PopconfirmDeleteIcon";
import StatusTag from "@/components/tags/StatusTag";
import { paginationTableProps, tableStandardProps } from "@/utils/componentUtils";
import { formatLocaleCurrency } from "@/utils/formatUtils";
import { Divider, Popconfirm, Table } from "antd";
import { ColumnsType } from "antd/es/table";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDeleteCommissionsUnit } from "../../../../../common/api/queries";
import { PageSizes, TableSizes } from "../../../../../common/constants";
import { Permission } from "../../../../../common/security/enums";
import { useHasPermissions } from "../../../../auth/hooks";
import InstitutionTypeTagWithName from "../../../../institution/components/InstitutionTypeTagWithName";
import { CommissionsBatchStep } from "../../enums";
import { CommissionsBatch, CommissionsUnit } from "../../types";
import { CommissionsUnitInputsTable } from "./CommissionsUnitInputsTable";

interface Props {
  batch: CommissionsBatch;
  onUpdateClick: (unit: CommissionsUnit) => void;
}

export const CommissionsUnitTable = ({ batch, onUpdateClick }: Props) => {
  const { t } = useTranslation();

  const hasManageCommissionsPermission = useHasPermissions(Permission.COMMISSIONS_MANAGE);

  const deleteCommissionsUnit = useDeleteCommissionsUnit();

  const columns = useMemo(() => {
    const items: ColumnsType<CommissionsUnit> = [
      {
        key: "institution",
        title: t("commissions.batch.attrs.unit.institution"),
        width: 232,
        ellipsis: { showTitle: false },
        render: (_, record) => <InstitutionTypeTagWithName institution={record.institution} ellipsis />,
      },
      {
        key: "allCommissionAmount",
        title: t("commissions.batch.attrs.unit.allCommissionAmount"),
        align: "right",
        width: 110,
        render: (_, record) => formatLocaleCurrency(record.allCommissionAmount ?? 0),
      },
      {
        key: "postponedCommissionAmount",
        title: t("commissions.batch.attrs.unit.postponedCommissionAmount"),
        align: "right",
        width: 110,
        render: (_, record) => formatLocaleCurrency(record.postponedCommissionAmount ?? 0),
      },
      {
        key: "bankAccountCommissionAmount",
        title: t("commissions.batch.attrs.unit.bankAccountCommissionAmount"),
        align: "right",
        width: 110,
        render: (_, record) => formatLocaleCurrency(record.bankAccountCommissionAmount ?? 0),
      },
      {
        key: "invoiceCommissionAmount",
        title: t("commissions.batch.attrs.unit.invoiceCommissionAmount"),
        align: "right",
        width: 110,
        render: (_, record) => (
          <span
            className={record.allCommissionAmount !== record.invoiceCommissionAmount ? "highlighted-text" : undefined}
          >
            {formatLocaleCurrency(record.invoiceCommissionAmount ?? 0)}
          </span>
        ),
      },
      {
        key: "volumeCommissionAmount",
        title: t("commissions.batch.attrs.unit.volumeCommissionAmount"),
        align: "right",
        width: 110,
        render: (_, record) => formatLocaleCurrency(record.volumeCommissionAmount ?? 0),
      },
    ];

    if (batch.step !== CommissionsBatchStep.FINISH) {
      items.push({
        key: "statusTags",
        width: 140,
        render: (_, record) => {
          const inputs = batch.inputs.filter((input) => input.institution?.id === record.institution.id);

          return (
            <>
              {inputs.some((input) => input.hasImportErrors) && (
                <StatusTag status="error" tooltip={t("commissions.batch.helpers.importedAttachmentError")} />
              )}
              {inputs.some((input) => input.importInProgress) && <StatusTag status="processing" />}
            </>
          );
        },
      });
    }

    if (!hasManageCommissionsPermission) {
      return items;
    }

    items.push({
      key: "actions",
      align: "right",
      fixed: "right",
      width: 180,
      render: (_, record) => (
        <>
          <ActionTextIcon icon="edit" color="blue" text={t("common.edit")} onClick={() => onUpdateClick(record)} />

          {batch.step !== CommissionsBatchStep.FINISH ? (
            <>
              <Divider orientation="vertical" />

              <Popconfirm
                title={t("commissions.batch.helpers.deleteUnitConfirm")}
                icon={<PopconfirmDeleteIcon />}
                okText={t("common.yes")}
                cancelText={t("common.no")}
                okType="danger"
                onConfirm={() =>
                  deleteCommissionsUnit.mutate({ id: batch.id, object: { institutionId: record.institution.id } })
                }
              >
                <ActionTextIcon
                  icon="delete"
                  color="red"
                  text={t("common.delete")}
                  disabled={
                    batch.stepChangeInProgress ||
                    batch.inputs
                      .filter((input) => input.institution?.id === record.institution.id)
                      .some((input) => input.importInProgress)
                  }
                />
              </Popconfirm>
            </>
          ) : undefined}
        </>
      ),
    });

    return items;
  }, [batch, hasManageCommissionsPermission]);

  return (
    <Table<CommissionsUnit>
      {...tableStandardProps()}
      columns={columns}
      scroll={{ x: TableSizes.LARGE }}
      dataSource={batch.commissionsUnits}
      expandable={{
        expandedRowRender: (record) => <CommissionsUnitInputsTable batch={batch} unit={record} />,
      }}
      pagination={{
        ...paginationTableProps,
        pageSize: PageSizes.MEDIUM,
        total: batch.commissionsUnits.length,
      }}
    />
  );
};
